import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

export default function CareerPreferencesStep2({ onSubmit, onComplete }) {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language.startsWith("ar");

  // -------------------------------------------------------
  //  STATE
  // -------------------------------------------------------
  const [sectionIdx, setSectionIdx] = useState(0);
  const [questionIdx, setQuestionIdx] = useState(0);
  const [selectedOption, setSelectedOption] = useState("");
  const [selectedMulti, setSelectedMulti] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // NEW POPUP (same as QuestionnaireStep1)
  const [popup, setPopup] = useState({
    visible: false,
    type: "",
    title: "",
    message: "",
  });

  const [answers, setAnswers] = useState({
    personality: {},
    values: {},
    skills: {},
    interests: {},
    workStyle: "",
  });

  const multiChoiceKeys = ["bestAt", "specialTalents"];

  // -------------------------------------------------------
  //   showPopup() — identical to QuestionnaireStep1
  // -------------------------------------------------------
  const showPopup = (type, title, message) => {
    setPopup({ visible: true, type, title, message });

    setTimeout(() => {
      setPopup({
        visible: false,
        type: "",
        title: "",
        message: "",
      });
    }, 2200);
  };
// ✅ Restore previously selected answer when navigating back

  // -------------------------------------------------------
  // Questionnaire Structure
  // -------------------------------------------------------
  const structure = useMemo(
    () => ({
      sections: [
        {
          title: t("🌟 Personality Orientation"),
          key: "personality",
          questions: [
            {
              key: "workPreference",
              q: t("Do you prefer working:"),
              opts: [t("With people"), t("With tasks/objects"), t("Both equally")],
            },
            {
              key: "routinePreference",
              q: t("Do you enjoy:"),
              opts: [t("Structured routines"), t("Variety and change"), t("No preference")],
            },
            {
              key: "decisionMaking",
              q: t("When making decisions, you rely more on:"),
              opts: [t("Logic and facts"), t("Feelings and intuition")],
            },
            {
              key: "planningStyle",
              q: t("Do you usually:"),
              opts: [t("Plan ahead carefully"), t("Adapt as things happen")],
            },
            {
              key: "groupComfort",
              q: t("In a group, you feel more comfortable:"),
              opts: [
                t("Leading the group"),
                t("Supporting and helping"),
                t("Working quietly on your own"),
              ],
            },
          ],
        },
        {
          title: t("💼 Work Values"),
          key: "values",
          questions: [
            {
              key: "jobImportance",
              q: t("What matters most in your job?"),
              opts: [
                t("Stability/security"),
                t("Growth opportunities"),
                t("Good salary"),
                t("Work-life balance"),
              ],
            },
            {
              key: "meaningfulWork",
              q: t("Which type of work feels most meaningful to you?"),
              opts: [
                t("Helping people directly"),
                t("Building/repairing things"),
                t("Creating/designing"),
                t("Managing/organizing"),
              ],
            },
            {
              key: "jobPredictability",
              q: t("Do you prefer a job that is:"),
              opts: [t("Predictable"), t("Flexible and varied")],
            },
            {
              key: "relocation",
              q: t("Would you relocate for better opportunities?"),
              opts: [t("Yes"), t("No"), t("Maybe")],
            },
            {
              key: "companySize",
              q: t("Do you prefer working:"),
              opts: [
                t("For a large company"),
                t("For a small company/startup"),
                t("Self-employed/freelance"),
              ],
            },
          ],
        },
        {
          title: t("🧠 Skills & Talents"),
          key: "skills",
          questions: [
            {
              key: "bestAt",
              q: t("Which of these are you best at?"),
              opts: [
                t("Talking to people / persuading"),
                t("Analyzing numbers or data"),
                t("Fixing/building things"),
                t("Creative arts / design"),
                t("Sports / fitness"),
                t("Organizing / planning"),
              ],
            },
            {
              key: "computer",
              q: t("How comfortable are you with computers?"),
              opts: [t("Advanced user"), t("Basic use only"), t("Not comfortable")],
            },
            {
              key: "problemSolving",
              q: t("Which problems do you enjoy solving?"),
              opts: [
                t("People issues"),
                t("Technical challenges"),
                t("Creative challenges"),
                t("Everyday practical issues"),
              ],
            },
            {
              key: "specialTalents",
              q: t("Do you have any special talents?"),
              opts: [
                t("Music / Singing"),
                t("Painting / Drawing"),
                t("Leadership"),
                t("Writing"),
                t("Sports"),
                t("Other"),
              ],
            },
            {
              key: "detailType",
              q: t("Which describes you better?"),
              opts: [t("Detail-oriented"), t("Big-picture thinker")],
            },
          ],
        },
        {
          title: t("🎨 Interests (Holland Code)"),
          key: "interests",
          questions: [
            { key: "likesBuildingThings", q: t("Do you enjoy building/fixing things with your hands?"), opts: [t("Yes"), t("No")] },
            { key: "likesScienceMath", q: t("Do you enjoy solving science or math problems?"), opts: [t("Yes"), t("No")] },
            { key: "likesArtsPerformance", q: t("Do you enjoy writing, painting, or performing?"), opts: [t("Yes"), t("No")] },
            { key: "likesHelping", q: t("Do you enjoy teaching, helping, or caring for others?"), opts: [t("Yes"), t("No")] },
            { key: "likesLeading", q: t("Do you enjoy leading people, selling ideas, or taking initiative?"), opts: [t("Yes"), t("No")] },
            { key: "likesOrganizing", q: t("Do you enjoy organizing data, files, or records?"), opts: [t("Yes"), t("No")] },
          ],
        },
        {
          title: t("🧭 Work Style"),
          key: "workStyle",
          questions: [
            {
              key: "workStyle",
              q: t("Do you prefer:"),
              opts: [t("Working alone"), t("Working in a team"), t("Both equally")],
            },
          ],
        },
      ],
    }),
    [t, i18n.language]
  );

  const section = structure.sections[sectionIdx];
  const question = section.questions[questionIdx];
useEffect(() => {
  // Get previously saved answer (if any)
  const saved =
    answers?.[section.key]?.[question.key] ||
    (section.key === "workStyle" ? answers.workStyle : null);

  if (!saved) {
    // No previous answer for this question
    setSelectedOption("");
    setSelectedMulti([]);
    return;
  }

  // Restore based on type
  if (Array.isArray(saved)) {
    setSelectedMulti(saved);
    setSelectedOption("");
  } else {
    setSelectedOption(saved);
    setSelectedMulti([]);
  }
}, [sectionIdx, questionIdx]);

  const isMulti = multiChoiceKeys.includes(question.key);
  const isLast =
    sectionIdx === structure.sections.length - 1 &&
    questionIdx === section.questions.length - 1;

  // -------------------------------------------------------
  //  Selection handlers
  // -------------------------------------------------------
  const handleSelect = (opt, qKey) => {
    if (isMulti) {
      setSelectedMulti((prev) =>
        prev.includes(opt)
          ? prev.filter((item) => item !== opt)
          : [...prev, opt]
      );
    } else {
      setSelectedOption(opt);
    }
  };

  // -------------------------------------------------------
  // Next / Prev
  // -------------------------------------------------------
  const handleNext = () => {
    const value = isMulti ? [...selectedMulti] : selectedOption;

    if (!value || (isMulti && value.length === 0)) return;

    setAnswers((prev) => ({
      ...prev,
      [section.key]:
        section.key === "workStyle"
          ? value
          : { ...(prev[section.key] || {}), [question.key]: value },
    }));

    if (questionIdx < section.questions.length - 1) {
      setQuestionIdx((i) => i + 1);
    } else {
      setSectionIdx((s) => s + 1);
      setQuestionIdx(0);
    }

    setSelectedOption("");
    setSelectedMulti([]);
  };

  const handlePrev = () => {
    if (questionIdx > 0) {
      setQuestionIdx((i) => i - 1);
    } else if (sectionIdx > 0) {
      const prevSec = structure.sections[sectionIdx - 1];
      setSectionIdx((s) => s - 1);
      setQuestionIdx(prevSec.questions.length - 1);
    }
  };

  // -------------------------------------------------------
  // Submit Handler — now uses showPopup()
  // -------------------------------------------------------
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const userId = localStorage.getItem("userId");
      const token = localStorage.getItem("token");

      if (!userId) throw new Error("User not logged in.");

      const p = answers.personality || {};
      const v = answers.values || {};
      const s = answers.skills || {};
      const i = answers.interests || {};

      const dto = {
        userId,
        Language: i18n.language || "en",
        workPreference: p.workPreference || "",
        routinePreference: p.routinePreference || "",
        decisionMaking: p.decisionMaking || "",
        planningStyle: p.planningStyle || "",
        groupComfort: p.groupComfort || "",
        jobImportance: v.jobImportance || "",
        meaningfulWork: v.meaningfulWork || "",
        jobPredictability: v.jobPredictability || "",
        relocation: v.relocation || "",
        companySize: v.companySize || "",
        bestAt: Array.isArray(s.bestAt) ? s.bestAt.join(", ") : "",
        computer: s.computer || "",
        problemSolving: s.problemSolving || "",
        specialTalents: Array.isArray(s.specialTalents)
          ? s.specialTalents
          : s.specialTalents
          ? [s.specialTalents]
          : [],
        detailType: s.detailType || "",
        q0: i.likesBuildingThings === "Yes",
        q1: i.likesScienceMath === "Yes",
        q2: i.likesArtsPerformance === "Yes",
        q3: i.likesHelping === "Yes",
        q4: i.likesLeading === "Yes",
        q5: i.likesOrganizing === "Yes",
        workStyle: answers.workStyle || "",
      };

      const res = await fetch(
        `http://103.104.48.64:5276/api/questionnaire/part2?userId=${userId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(dto),
        }
      );

      if (!res.ok) throw new Error(await res.text());

      // ---------------------------
      // SUCCESS POPUP
      // ---------------------------
      showPopup(
        "success",
        isArabic ? "تم حفظ تفضيلاتك بنجاح!" : "Saved Successfully!",
        isArabic
          ? "تم تسجيل تفضيلاتك المهنية."
          : "Your career preferences have been saved."
      );

      setTimeout(() => {
        onSubmit?.();
        onComplete?.();
      }, 2200);
    } catch (err) {
      showPopup("error", "Error", err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------
  // RENDER
  // -------------------------------------------------------
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        dir={isArabic ? "rtl" : "ltr"}
        className="relative flex flex-col justify-between items-center text-center bg-[#141428]/90 border border-[#EB6AB5]/20 rounded-2xl shadow-2xl backdrop-blur-md w-full max-w-sm mx-auto p-8"
      >
        <div>
          <h2 className="text-2xl font-bold text-[#EB6AB5] mb-3">
            {section.title}
          </h2>

          <p className="text-gray-300 text-sm mb-6">{question.q}</p>

          <div
            className={`grid ${
              question.opts.length <= 3 ? "grid-cols-1" : "grid-cols-2"
            } gap-4`}
          >
            {question.opts.map((opt) => {
              const isActive = isMulti
                ? selectedMulti.includes(opt)
                : selectedOption === opt;

              return (
                <button
                  key={opt}
                  onClick={() =>
                    questionIdx === section.questions.length - 1 &&
                    sectionIdx === structure.sections.length - 1
                      ? handleSubmit()
                      : handleSelect(opt, question.key)
                  }
                  disabled={submitting}
                  className={`py-3 rounded-lg border transition text-sm font-medium ${
                    isActive
                      ? "bg-[#EB6AB5]/40 border-[#EB6AB5] text-white"
                      : "bg-[#1c1c2e] border-[#EB6AB5]/20 hover:bg-[#EB6AB5]/25 text-gray-200"
                  }`}
                >
                  {opt}
                </button>
              );
            })}
          </div>

          {isMulti && selectedMulti.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {selectedMulti.map((item) => (
                <span
                  key={item}
                  className="text-xs bg-[#EB6AB5]/30 border border-[#EB6AB5]/50 rounded-full px-3 py-1 text-white"
                >
                  {item}
                </span>
              ))}
            </div>
          )}
        </div>

        <div
          className={`flex justify-between items-center w-full mt-6 ${
            isArabic ? "flex-row-reverse" : ""
          }`}
        >
          <button
            onClick={handlePrev}
            disabled={sectionIdx === 0 && questionIdx === 0}
            className="px-5 py-2 rounded-full text-gray-400 hover:text-white text-sm"
          >
            {isArabic ? t("السابق →") : t("← Previous")}
          </button>

          <button
            onClick={isLast ? handleSubmit : handleNext}
            disabled={
              submitting ||
              (isMulti ? selectedMulti.length === 0 : !selectedOption)
            }
            className="px-6 py-2 rounded-full font-semibold text-sm bg-[#EB6AB5] hover:bg-[#ff83d1] text-[#0F0A24]"
          >
            {isLast ? t("Submit →") : t("Next →")}
          </button>
        </div>
      </motion.div>

      {/* --------------------------------------------------- */}
      {/*      POPUP — SAME AS CONSENT + QUESTIONNAIRE         */}
      {/* --------------------------------------------------- */}
      {popup.visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-md z-[9999]"
          dir={isArabic ? "rtl" : "ltr"}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="bg-[#1b1b2b] border border-[#EB6AB5]/40 rounded-3xl shadow-2xl p-8 text-center max-w-xs w-[90%]"
          >
            <h3 className="text-xl font-semibold text-[#EB6AB5]">
              {popup.title}
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed mt-2">
              {popup.message}
            </p>
          </motion.div>
        </motion.div>
      )}
    </>
  );
}
